import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { HardHat, Loader2, Lock, Mail } from "lucide-react";

export default function Login() {
  const { login, verify2fa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("creds");
  const [preAuth, setPreAuth] = useState("");
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) navigate("/");
    else if (res.twofa_required) { setPreAuth(res.pre_auth_token); setStep("2fa"); setCode(""); }
    else setError(res.error);
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await verify2fa(preAuth, code.trim());
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="flex min-h-screen">
      {/* Left visual */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-slate-900 p-12 lg:flex">
        <div className="grain absolute inset-0 opacity-60" />
        <img
          src="https://images.unsplash.com/photo-1788430641879-fc7c53c67563?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="chantier"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="relative flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500">
            <HardHat className="h-6 w-6 text-slate-900" />
          </div>
          <span className="font-head text-xl font-extrabold text-white">IR INGENIERIE</span>
        </div>
        <div className="relative">
          <h1 className="font-head text-4xl font-extrabold leading-tight text-white lg:text-5xl">
            Vos bons d'intervention,<br /><span className="text-amber-400">du terrain au bureau.</span>
          </h1>
          <p className="mt-4 max-w-md text-base leading-relaxed text-slate-300">
            Remplissez PV de libération des emprises et journaux de chantier sur tablette,
            faites signer, ajoutez vos photos et envoyez le PDF en un clic.
          </p>
        </div>
        <div className="relative text-sm text-slate-400">Copyright NetKom Informatique 2026</div>
      </div>

      {/* Form */}
      <div className="flex w-full items-center justify-center bg-slate-50 p-6 dark:bg-slate-950 lg:w-1/2">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500">
                <HardHat className="h-6 w-6 text-slate-900" />
              </div>
              <span className="font-head text-xl font-extrabold">IR INGENIERIE</span>
            </div>
          </div>
          <h2 className="font-head text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Connexion</h2>
          <p className="mt-2 text-sm text-slate-500">
            {step === "2fa" ? "Saisissez le code à 6 chiffres de votre application d'authentification." : "Accédez à votre espace technicien ou administrateur."}
          </p>

          {step === "2fa" ? (
            <form onSubmit={submit2fa} className="mt-8 space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-xs font-semibold text-slate-500">Code de vérification</span>
                <input type="text" required value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                  data-testid="login-2fa-code" placeholder="123456" inputMode="numeric" autoComplete="one-time-code" autoFocus
                  className="h-14 w-full rounded-lg border border-slate-300 bg-white px-3 text-center text-2xl font-bold tracking-[0.4em] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-900" />
              </label>
              {error && (
                <div data-testid="login-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">{error}</div>
              )}
              <Button type="submit" disabled={loading || code.length < 6} data-testid="login-2fa-submit"
                className="h-12 w-full bg-amber-500 text-base font-semibold text-slate-900 hover:bg-amber-400">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Vérifier"}
              </Button>
              <button type="button" onClick={() => { setStep("creds"); setError(""); setCode(""); }} data-testid="login-2fa-back"
                className="w-full text-sm font-semibold text-slate-500 hover:text-slate-700">Retour</button>
            </form>
          ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold text-slate-500">Email</span>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email" placeholder="vous@entreprise.com"
                  name="email" autoComplete="username" inputMode="email"
                  className="h-12 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-900" />
              </div>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold text-slate-500">Mot de passe</span>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password" placeholder="••••••••"
                  name="password" autoComplete="current-password"
                  className="h-12 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-900" />
              </div>
            </label>

            <div className="flex justify-end -mt-1">
              <Link to="/forgot-password" data-testid="forgot-password-link"
                className="text-sm font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400">
                Mot de passe oublié ?
              </Link>
            </div>

            {error && (
              <div data-testid="login-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
                {error}
              </div>
            )}

            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="h-12 w-full bg-amber-500 text-base font-semibold text-slate-900 hover:bg-amber-400">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Se connecter"}
            </Button>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}
